'use client'

import { useEffect, useMemo, useState } from 'react'
import { adminApi, horseAdminApi } from '../../api/endpoints'
import { Horse, HorseCourse, HorsePollMode, HorsePollRound } from '../../types'
import Button from '../ui/Button'
import Card from '../ui/Card'

type HorseAdminTab = 'guide' | 'courses' | 'results' | 'polls' | 'rounds'

const TABS: { id: HorseAdminTab; label: string; step: string }[] = [
  { id: 'guide', label: 'راهنما', step: '?' },
  { id: 'courses', label: '۱. کورس', step: '۱' },
  { id: 'polls', label: '۲. نظرسنجی', step: '۲' },
  { id: 'results', label: '۳. نتیجه', step: '۳' },
  { id: 'rounds', label: '۴. تسویه', step: '۴' },
]

const ROUND_MODES: {
  id: HorsePollMode
  label: string
  courses: number
  coursesMin?: number
  coursesMax?: number
  fee: string
  hint: string
}[] = [
  {
    id: 'league',
    label: 'هورس لیگ',
    courses: 15,
    coursesMin: 5,
    coursesMax: 15,
    fee: 'رایگان',
    hint: '۵ تا ۱۵ کورس پشت‌هم (بر اساس زمان). از هر کورس ۱ اسب؛ امتیاز ۹…۱؛ برنده طلا؛ تاپ ماهانه.',
  },
  {
    id: 'three_in_four',
    label: 'سه در چهار',
    courses: 4,
    fee: '۲ برنز یا ۵ ویدیو',
    hint: '۴ کورس · از هر کورس ۴ اسب فعال · کاربر در هر کورس ترتیب ۱…۴ را مشخص می‌کند · جمع امتیاز · طلا.',
  },
  {
    id: 'twins',
    label: 'دو قلو',
    courses: 5,
    fee: '۲ برنز یا ۵ ویدیو',
    hint: '۵ کورس · از هر کورس ۲ اسب · اول‌ودوم → درست؛ ۳ درست طلا / ۵ درست الماس.',
  },
  {
    id: 'three_one',
    label: 'سه تا یکی',
    courses: 5,
    fee: '۲ برنز یا ۵ ویدیو',
    hint: '۵ کورس · از هر کورس ۳ اسب · یکی باید اول شود؛ ۵ اول → طلا.',
  },
  {
    id: 'more_chance',
    label: 'شانس بیشتر',
    courses: 5,
    fee: '۱ برنز یا ۳ ویدیو',
    hint: '۵ کورس · از هر کورس ۲ اسب · بهترین مقام؛ امتیاز ۹…۱؛ ≥۴۳ → طلا.',
  },
]

const MODE_LABEL: Record<string, string> = Object.fromEntries(
  ROUND_MODES.map((m) => [m.id, m.label]),
)

const STATUS_LABEL: Record<string, { text: string; className: string }> = {
  upcoming: { text: 'در انتظار', className: 'bg-amber-500/20 text-amber-300' },
  finished: { text: 'تمام‌شده', className: 'bg-emerald-500/20 text-emerald-300' },
  open: { text: 'باز', className: 'bg-sky-500/20 text-sky-300' },
  closed: { text: 'بسته', className: 'bg-gray-500/20 text-gray-300' },
  settled: { text: 'تسویه‌شده', className: 'bg-emerald-500/20 text-emerald-300' },
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_LABEL[status] || { text: status, className: 'bg-gray-700 text-gray-300' }
  return (
    <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${s.className}`}>
      {s.text}
    </span>
  )
}

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-1">
      <label className="block text-sm font-medium text-gray-200">{children}</label>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
  )
}

const inputClass =
  'w-full bg-gray-900/60 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-primary'

type HorseFormRow = { name: string; photo_url: string }

const emptyHorseRows = (n: number): HorseFormRow[] =>
  Array.from({ length: n }, () => ({ name: '', photo_url: '' }))

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function AdminHorsePanel() {
  const [tab, setTab] = useState<HorseAdminTab>('guide')
  const [courses, setCourses] = useState<HorseCourse[]>([])
  const [rounds, setRounds] = useState<HorsePollRound[]>([])
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)

  // Create / edit course
  const [editingCourseId, setEditingCourseId] = useState<number | null>(null)
  const [courseName, setCourseName] = useState('')
  const [startTime, setStartTime] = useState('')
  const [horseRows, setHorseRows] = useState<HorseFormRow[]>(() => emptyHorseRows(8))
  const [photoBusy, setPhotoBusy] = useState<number | null>(null)

  // Create poll
  const [roundMode, setRoundMode] = useState<HorsePollMode>('league')
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([])
  // courseId -> horseIds for three_in_four (exactly 4 per course)
  const [lockedByCourse, setLockedByCourse] = useState<Record<number, number[]>>({})

  // Results: courseId -> horseId -> place
  const [resultCourseId, setResultCourseId] = useState<number>(0)
  const [placeByHorse, setPlaceByHorse] = useState<Record<number, number>>({})

  const roundModeCfg = ROUND_MODES.find((m) => m.id === roundMode)
  const needCoursesMin = roundModeCfg?.coursesMin ?? roundModeCfg?.courses ?? 5
  const needCoursesMax = roundModeCfg?.coursesMax ?? roundModeCfg?.courses ?? 5
  const needCourses = needCoursesMax
  const modeMeta = ROUND_MODES.find((m) => m.id === roundMode)!

  const upcomingCourses = useMemo(
    () => courses.filter((c) => c.status === 'upcoming'),
    [courses],
  )
  const finishedCourses = useMemo(
    () => courses.filter((c) => c.status === 'finished'),
    [courses],
  )
  const openRounds = useMemo(() => rounds.filter((r) => r.status !== 'settled'), [rounds])
  const settledRounds = useMemo(() => rounds.filter((r) => r.status === 'settled'), [rounds])
  const courseIdsInUse = useMemo(() => {
    const ids = new Set<number>()
    for (const r of rounds) {
      for (const rc of r.courses || []) {
        ids.add(rc.course_id)
      }
    }
    return ids
  }, [rounds])
  const editingInUse = editingCourseId != null && courseIdsInUse.has(editingCourseId)

  const resultCourse = courses.find((c) => c.id === resultCourseId)
  const selectedCoursesOrdered = selectedCourseIds
    .map((id) => courses.find((c) => c.id === id))
    .filter(Boolean) as HorseCourse[]

  const flash = (ok: string | null, err: string | null) => {
    setMsg(ok)
    setError(err)
    if (ok || err) {
      window.setTimeout(() => {
        setMsg(null)
        setError(null)
      }, 4500)
    }
  }

  const refresh = async () => {
    setLoading(true)
    try {
      const [c, r] = await Promise.all([
        horseAdminApi.listCourses(100, 0),
        horseAdminApi.listRounds(),
      ])
      setCourses(c.courses || [])
      setRounds(r.rounds || [])
    } catch (e: any) {
      flash(null, e?.response?.data?.error || e?.message || 'خطا در بارگذاری')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  const setHorseCount = (n: number) => {
    if (editingInUse) return
    const count = Math.min(20, Math.max(4, n))
    setHorseRows((prev) => {
      const next = [...prev]
      while (next.length < count) next.push({ name: '', photo_url: '' })
      return next.slice(0, count)
    })
  }

  const resetCourseForm = () => {
    setEditingCourseId(null)
    setCourseName('')
    setStartTime('')
    setHorseRows(emptyHorseRows(8))
    setPhotoBusy(null)
  }

  const startEditCourse = (c: HorseCourse) => {
    if (c.status !== 'upcoming') {
      flash(null, 'کورس نتیجه‌دار قابل ویرایش نیست')
      return
    }
    const sorted = [...(c.horses || [])].sort((a, b) => a.number - b.number)
    setEditingCourseId(c.id)
    setCourseName(c.name)
    setStartTime(c.start_time ? toDatetimeLocalValue(c.start_time) : '')
    setHorseRows(
      sorted.length >= 4
        ? sorted.map((h) => ({ name: h.name, photo_url: h.photo_url || '' }))
        : emptyHorseRows(8),
    )
    setTab('courses')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const uploadHorsePhoto = async (index: number, file: File) => {
    setPhotoBusy(index)
    setError(null)
    try {
      const res = await adminApi.uploadPlayerPhoto(file)
      setHorseRows((prev) => prev.map((row, i) => (i === index ? { ...row, photo_url: res.url } : row)))
    } catch (e: any) {
      flash(null, e?.response?.data?.message || e?.response?.data?.error || e?.message || 'آپلود عکس ناموفق')
    } finally {
      setPhotoBusy(null)
    }
  }

  const saveCourse = async () => {
    const rows = horseRows.map((row) => ({ name: row.name.trim(), photo_url: row.photo_url }))
    const named = rows.filter((row) => row.name)
    if (!courseName.trim()) {
      flash(null, 'نام کورس را بنویس')
      return
    }
    if (!startTime) {
      flash(null, 'تاریخ و ساعت شروع را مشخص کن')
      return
    }
    if (named.length < 4 || named.length > 20) {
      flash(null, 'حداقل ۴ و حداکثر ۲۰ اسب با نام لازم است')
      return
    }
    if (named.length !== horseRows.length) {
      flash(null, 'همه خانه‌های اسب باید پر شوند')
      return
    }
    const payload = {
      name: courseName.trim(),
      start_time: new Date(startTime).toISOString(),
      horses: named.map((row, i) => ({ name: row.name, number: i + 1, photo_url: row.photo_url })),
    }
    setBusy(true)
    try {
      if (editingCourseId != null) {
        await horseAdminApi.updateCourse(editingCourseId, payload)
        resetCourseForm()
        await refresh()
        flash('کورس به‌روز شد', null)
      } else {
        await horseAdminApi.createCourse(payload)
        resetCourseForm()
        await refresh()
        flash('کورس ساخته شد — حالا می‌توانی نظرسنجی بسازی', null)
        setTab('polls')
      }
    } catch (e: any) {
      flash(
        null,
        e?.response?.data?.error ||
          e?.message ||
          (editingCourseId != null ? 'ویرایش کورس ناموفق' : 'ساخت کورس ناموفق'),
      )
    } finally {
      setBusy(false)
    }
  }

  const removeCourse = async (c: HorseCourse) => {
    let usage: { round_count: number; entry_count: number }
    try {
      ;({ usage } = await horseAdminApi.getCourseUsage(c.id))
    } catch (e: any) {
      flash(null, e?.response?.data?.error || e?.message || 'خواندن وضعیت کورس ناموفق')
      return
    }
    const warn =
      usage.round_count > 0
        ? `کورس «${c.name}» در ${usage.round_count} نظرسنجی است و ${usage.entry_count} نفر شرکت کرده‌اند.\nبا حذف، آن نظرسنجی‌ها و شرکت‌کننده‌ها هم پاک می‌شوند.\nادامه؟`
        : `کورس «${c.name}» حذف شود؟`
    if (!window.confirm(warn)) return
    setBusy(true)
    try {
      await horseAdminApi.deleteCourse(c.id)
      if (editingCourseId === c.id) resetCourseForm()
      if (resultCourseId === c.id) {
        setResultCourseId(0)
        setPlaceByHorse({})
      }
      setSelectedCourseIds((prev) => prev.filter((id) => id !== c.id))
      await refresh()
      flash(
        usage.round_count > 0
          ? `کورس و ${usage.round_count} نظرسنجی مرتبط حذف شد`
          : 'کورس حذف شد',
        null,
      )
    } catch (e: any) {
      flash(null, e?.response?.data?.error || e?.message || 'حذف کورس ناموفق')
    } finally {
      setBusy(false)
    }
  }

  const toggleCourse = (id: number) => {
    setSelectedCourseIds((prev) => {
      if (prev.includes(id)) {
        setLockedByCourse((locks) => {
          const next = { ...locks }
          delete next[id]
          return next
        })
        return prev.filter((x) => x !== id)
      }
      if (prev.length >= needCoursesMax) {
        flash(
          null,
          needCoursesMin === needCoursesMax
            ? `برای این حالت فقط ${needCoursesMax} کورس لازم است`
            : `حداکثر ${needCoursesMax} کورس می‌توانی انتخاب کنی`,
        )
        return prev
      }
      return [...prev, id]
    })
  }

  const toggleLockedHorse = (courseId: number, horseId: number) => {
    setLockedByCourse((prev) => {
      const cur = prev[courseId] || []
      if (cur.includes(horseId)) {
        return { ...prev, [courseId]: cur.filter((id) => id !== horseId) }
      }
      if (cur.length >= 4) {
        flash(null, 'از هر کورس حداکثر ۴ اسب می‌توانی فعال کنی')
        return prev
      }
      return { ...prev, [courseId]: [...cur, horseId] }
    })
  }

  const createRound = async () => {
    if (selectedCourseIds.length < needCoursesMin || selectedCourseIds.length > needCoursesMax) {
      flash(
        null,
        needCoursesMin === needCoursesMax
          ? `دقیقاً ${needCoursesMax} کورس انتخاب کن`
          : `بین ${needCoursesMin} تا ${needCoursesMax} کورس پشت‌هم (بر اساس زمان) انتخاب کن`,
      )
      return
    }
    if (roundMode === 'three_in_four') {
      const incomplete = selectedCourseIds.filter((cid) => (lockedByCourse[cid] || []).length !== 4)
      if (incomplete.length > 0) {
        flash(null, 'از هر کورس انتخاب‌شده دقیقاً ۴ اسب فعال کن (جمع ۱۶ اسب)')
        return
      }
      const tooFewHorses = selectedCourseIds.some((cid) => {
        const c = courses.find((x) => x.id === cid)
        return (c?.horses?.length || 0) < 4
      })
      if (tooFewHorses) {
        flash(null, 'هر کورس باید حداقل ۴ اسب داشته باشد')
        return
      }
    }
    setBusy(true)
    try {
      await horseAdminApi.createRound({
        mode: roundMode,
        course_ids: selectedCourseIds,
        locked_horse_ids:
          roundMode === 'three_in_four'
            ? selectedCourseIds.flatMap((cid) => lockedByCourse[cid] || [])
            : undefined,
      })
      setSelectedCourseIds([])
      setLockedByCourse({})
      await refresh()
      flash('نظرسنجی باز شد — کاربران می‌توانند شرکت کنند', null)
      setTab('rounds')
    } catch (e: any) {
      flash(null, e?.response?.data?.error || e?.message || 'ساخت نظرسنجی ناموفق')
    } finally {
      setBusy(false)
    }
  }

  const openResultEditor = (courseId: number) => {
    const course = courses.find((c) => c.id === courseId)
    setResultCourseId(courseId)
    const initial: Record<number, number> = {}
    ;(course?.horses || []).forEach((h, i) => {
      initial[h.id] = h.finish_position || i + 1
    })
    setPlaceByHorse(initial)
    setTab('results')
  }

  const setPlace = (horseId: number, place: number) => {
    setPlaceByHorse((prev) => {
      const next = { ...prev }
      // swap if another horse already has this place
      const other = Object.entries(next).find(
        ([id, p]) => Number(id) !== horseId && p === place,
      )
      const oldPlace = next[horseId]
      if (other) {
        next[Number(other[0])] = oldPlace
      }
      next[horseId] = place
      return next
    })
  }

  const submitResult = async () => {
    if (!resultCourse || !resultCourse.horses?.length) {
      flash(null, 'اول یک کورس انتخاب کن')
      return
    }
    const horses = resultCourse.horses
    const places = horses.map((h) => placeByHorse[h.id])
    if (places.some((p) => !p || p < 1 || p > horses.length)) {
      flash(null, 'برای هر اسب یک مقام معتبر بگذار')
      return
    }
    if (new Set(places).size !== horses.length) {
      flash(null, 'مقام‌ها نباید تکراری باشند')
      return
    }
    setBusy(true)
    try {
      await horseAdminApi.setResult(resultCourse.id, placeByHorse)
      await refresh()
      flash('نتیجه ثبت شد — اگر همه کورس‌های یک دور تمام شدند، تسویه کن', null)
      setTab('rounds')
    } catch (e: any) {
      flash(null, e?.response?.data?.error || e?.message || 'ثبت نتیجه ناموفق')
    } finally {
      setBusy(false)
    }
  }

  const closeVoting = async (id: number) => {
    if (!window.confirm('ثبت نظر برای این نظرسنجی بسته شود؟ کاربران دیگر نمی‌توانند شرکت کنند.')) {
      return
    }
    setBusy(true)
    try {
      await horseAdminApi.closeRound(id)
      await refresh()
      flash('ثبت نظر بسته شد', null)
    } catch (e: any) {
      flash(null, e?.response?.data?.error || e?.message || 'بستن ثبت نظر ناموفق')
    } finally {
      setBusy(false)
    }
  }

  const settle = async (id: number) => {
    setBusy(true)
    try {
      await horseAdminApi.settleRound(id)
      await refresh()
      flash('امتیازها و جوایز این دور اعمال شد', null)
    } catch (e: any) {
      flash(null, e?.response?.data?.error || e?.message || 'تسویه ناموفق — همه نتایج ثبت شده؟')
    } finally {
      setBusy(false)
    }
  }

  const sortedHorsesForResult = (horses: Horse[]) =>
    [...horses].sort((a, b) => (placeByHorse[a.id] || 99) - (placeByHorse[b.id] || 99))

  return (
    <div className="space-y-4">
      {/* Header + progress */}
      <Card className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-bold text-lg">🐎 مدیریت نظرسنجی اسب</h2>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              ترتیب کار: کورس بساز → نظرسنجی باز کن → بعد از مسابقه نتیجه بزن → تسویه کن
            </p>
          </div>
          <button
            type="button"
            onClick={() => refresh()}
            className="text-xs text-gray-400 hover:text-white shrink-0 px-2 py-1 border border-gray-600 rounded-lg"
          >
            {loading ? '…' : 'بروزرسانی'}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
          <div className="rounded-lg bg-gray-800/80 py-2">
            <div className="text-lg font-bold text-white">{upcomingCourses.length}</div>
            <div className="text-gray-400">کورس باز</div>
          </div>
          <div className="rounded-lg bg-gray-800/80 py-2">
            <div className="text-lg font-bold text-white">{openRounds.length}</div>
            <div className="text-gray-400">نظرسنجی فعال</div>
          </div>
          <div className="rounded-lg bg-gray-800/80 py-2">
            <div className="text-lg font-bold text-white">{finishedCourses.length}</div>
            <div className="text-gray-400">نتیجه‌دار</div>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto pb-0.5">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition ${
                tab === t.id
                  ? 'bg-primary text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-gray-200'
              }`}
            >
              <span className="opacity-70">{t.step}</span>
              {t.label}
            </button>
          ))}
        </div>
      </Card>

      {(error || msg) && (
        <div
          className={`p-3 rounded-xl text-sm text-center ${
            error ? 'bg-red-900/40 text-red-200' : 'bg-emerald-900/40 text-emerald-200'
          }`}
        >
          {error || msg}
        </div>
      )}

      {/* GUIDE */}
      {tab === 'guide' && (
        <Card className="p-4 space-y-4">
          <h3 className="font-bold">چطور کار می‌کند؟</h3>
          <ol className="space-y-3 text-sm text-gray-300">
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-primary/30 text-primary flex items-center justify-center text-xs font-bold">
                ۱
              </span>
              <div>
                <p className="font-medium text-white">کورس بساز</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  هر کورس یک مسابقه است (۴ تا ۲۰ اسب + تاریخ/ساعت). چند کورس برای دوره‌های مختلف بساز.
                </p>
                <button
                  type="button"
                  className="text-xs text-primary mt-1"
                  onClick={() => setTab('courses')}
                >
                  رفتن به ساخت کورس ←
                </button>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-primary/30 text-primary flex items-center justify-center text-xs font-bold">
                ۲
              </span>
              <div>
                <p className="font-medium text-white">نظرسنجی باز کن</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  حالت را انتخاب کن و کورس‌های لازم را به ترتیب تیک بزن. کاربران از اپ شرکت می‌کنند.
                </p>
                <button
                  type="button"
                  className="text-xs text-primary mt-1"
                  onClick={() => setTab('polls')}
                >
                  رفتن به نظرسنجی ←
                </button>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-primary/30 text-primary flex items-center justify-center text-xs font-bold">
                ۳
              </span>
              <div>
                <p className="font-medium text-white">نتیجه مسابقه را بزن</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  بعد از اتمام واقعی کورس، مقام هر اسب را با لیست کشویی ثبت کن (دیگر لازم نیست شناسه تایپ کنی).
                </p>
                <button
                  type="button"
                  className="text-xs text-primary mt-1"
                  onClick={() => setTab('results')}
                >
                  رفتن به نتیجه ←
                </button>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-primary/30 text-primary flex items-center justify-center text-xs font-bold">
                ۴
              </span>
              <div>
                <p className="font-medium text-white">تسویه نظرسنجی</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  وقتی همه کورس‌های یک دور نتیجه دارند، دکمه تسویه را بزن تا امتیاز و سکه طلا/الماس داده شود.
                </p>
                <button
                  type="button"
                  className="text-xs text-primary mt-1"
                  onClick={() => setTab('rounds')}
                >
                  رفتن به تسویه ←
                </button>
              </div>
            </li>
          </ol>
        </Card>
      )}

      {/* COURSES */}
      {tab === 'courses' && (
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-bold">
                {editingCourseId != null ? `ویرایش کورس #${editingCourseId}` : 'ساخت کورس جدید'}
              </h3>
              {editingCourseId != null && (
                <button
                  type="button"
                  className="text-xs text-gray-400 hover:text-white"
                  onClick={resetCourseForm}
                >
                  انصراف
                </button>
              )}
            </div>
            {editingInUse && (
              <p className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                این کورس در نظرسنجی است؛ نام و زمان را می‌توانی عوض کنی، ولی تعداد اسب‌ها ثابت می‌ماند.
              </p>
            )}
            <div>
              <FieldLabel hint="مثلاً: کورس عصر جمعه">نام کورس</FieldLabel>
              <input
                className={inputClass}
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                placeholder="نام کورس"
              />
            </div>
            <div>
              <FieldLabel hint="زمان شروع مسابقه">تاریخ و ساعت</FieldLabel>
              <input
                type="datetime-local"
                className={inputClass}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <FieldLabel hint="بین ۴ تا ۲۰">تعداد اسب</FieldLabel>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="w-10 h-10 rounded-lg border border-gray-600 text-lg disabled:opacity-40"
                  disabled={editingInUse}
                  onClick={() => setHorseCount(horseRows.length - 1)}
                >
                  −
                </button>
                <span className="flex-1 text-center font-bold text-lg">{horseRows.length}</span>
                <button
                  type="button"
                  className="w-10 h-10 rounded-lg border border-gray-600 text-lg disabled:opacity-40"
                  disabled={editingInUse}
                  onClick={() => setHorseCount(horseRows.length + 1)}
                >
                  +
                </button>
              </div>
            </div>
            <div>
              <FieldLabel hint="شماره اسب به‌ترتیب ۱، ۲، ۳… اختصاص داده می‌شود. عکس اختیاری است.">
                نام و عکس اسب‌ها
              </FieldLabel>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {horseRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-8 text-center text-xs text-gray-400 font-mono">#{i + 1}</span>
                    {row.photo_url ? (
                      <img src={row.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 bg-gray-800" />
                    ) : (
                      <span className="w-10 h-10 rounded-lg bg-gray-800 border border-gray-700 shrink-0" />
                    )}
                    <input
                      className={inputClass}
                      value={row.name}
                      onChange={(e) => {
                        const next = [...horseRows]
                        next[i] = { ...next[i], name: e.target.value }
                        setHorseRows(next)
                      }}
                      placeholder={`نام اسب ${i + 1}`}
                    />
                    <label className="shrink-0 text-[11px] px-2 py-2 rounded-lg border border-gray-600 cursor-pointer text-gray-300">
                      {photoBusy === i ? '…' : row.photo_url ? 'عوض' : 'عکس'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        disabled={photoBusy !== null}
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          e.target.value = ''
                          if (file) void uploadHorsePhoto(i, file)
                        }}
                      />
                    </label>
                    {row.photo_url && (
                      <button
                        type="button"
                        className="shrink-0 text-[11px] text-gray-400 px-1"
                        onClick={() => {
                          setHorseRows((prev) => prev.map((item, idx) => (idx === i ? { ...item, photo_url: '' } : item)))
                        }}
                      >
                        حذف
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <Button disabled={busy} onClick={saveCourse}>
              {busy
                ? editingCourseId != null
                  ? 'در حال ذخیره…'
                  : 'در حال ساخت…'
                : editingCourseId != null
                  ? 'ذخیره تغییرات'
                  : 'ساخت کورس و ادامه'}
            </Button>
          </Card>

          <Card className="p-4 space-y-2">
            <h3 className="font-bold text-sm">کورس‌های موجود ({courses.length})</h3>
            {courses.length === 0 && (
              <p className="text-xs text-gray-500">هنوز کورسی نیست — اول یکی بساز.</p>
            )}
            {courses.map((c) => {
              const inUse = courseIdsInUse.has(c.id)
              return (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-2 border border-gray-700 rounded-xl p-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{c.name}</span>
                      <StatusBadge status={c.status} />
                      {inUse && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300">
                          در نظرسنجی
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      #{c.id} · {c.horses?.length || 0} اسب ·{' '}
                      {c.start_time
                        ? new Date(c.start_time).toLocaleString('fa-IR')
                        : '—'}
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-wrap gap-1.5 justify-end">
                    {c.status === 'upcoming' && (
                      <>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => startEditCourse(c)}
                          className="text-xs px-2 py-1.5 rounded-lg border border-gray-600 text-gray-300"
                        >
                          ویرایش
                        </button>
                        <button
                          type="button"
                          onClick={() => openResultEditor(c.id)}
                          className="text-xs px-2 py-1.5 rounded-lg border border-gray-600 text-gray-300"
                        >
                          ثبت نتیجه
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      title={
                        inUse
                          ? 'حذف کورس و نظرسنجی‌های مرتبط'
                          : 'حذف کورس'
                      }
                      onClick={() => removeCourse(c)}
                      className="text-xs px-2 py-1.5 rounded-lg border border-red-500/40 text-red-300 disabled:opacity-40"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              )
            })}
          </Card>
        </div>
      )}

      {/* POLLS */}
      {tab === 'polls' && (
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="font-bold">ساخت نظرسنجی جدید</h3>

            <div>
              <FieldLabel>حالت بازی</FieldLabel>
              <div className="grid grid-cols-1 gap-2">
                {ROUND_MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      setRoundMode(m.id)
                      setSelectedCourseIds([])
                      setLockedByCourse({})
                    }}
                    className={`text-right rounded-xl border p-3 transition ${
                      roundMode === m.id
                        ? 'border-primary bg-primary/15'
                        : 'border-gray-700 bg-gray-900/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm">{m.label}</span>
                      <span className="text-[11px] text-gray-400">
                        {m.coursesMin && m.coursesMax && m.coursesMin !== m.coursesMax
                          ? `${m.coursesMin}–${m.coursesMax} کورس`
                          : `${m.courses} کورس`}{' '}
                        · {m.fee}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{m.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-gray-800/60 p-3">
              <div className="flex items-center justify-between mb-2">
                <FieldLabel hint="برای هورس لیگ کورس‌ها باید پشت‌هم بر اساس زمان باشند؛ سرور خودش مرتب می‌کند">
                  انتخاب کورس‌ها
                </FieldLabel>
                <span
                  className={`text-xs font-bold ${
                    selectedCourseIds.length >= needCoursesMin &&
                    selectedCourseIds.length <= needCoursesMax
                      ? 'text-emerald-400'
                      : 'text-amber-300'
                  }`}
                >
                  {selectedCourseIds.length}
                  {needCoursesMin === needCoursesMax
                    ? ` / ${needCoursesMax}`
                    : ` / ${needCoursesMin}–${needCoursesMax}`}
                </span>
              </div>

              {selectedCourseIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {selectedCoursesOrdered.map((c, i) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 text-[11px] bg-primary/25 text-primary-foreground px-2 py-1 rounded-full border border-primary/40"
                    >
                      <span className="font-bold">{i + 1}.</span>
                      {c.name}
                      <button
                        type="button"
                        className="opacity-70 hover:opacity-100"
                        onClick={() => toggleCourse(c.id)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {upcomingCourses.length === 0 && (
                <p className="text-xs text-amber-300/90 mb-2">
                  کورس بازی نداری.{' '}
                  <button type="button" className="underline" onClick={() => setTab('courses')}>
                    اول کورس بساز
                  </button>
                </p>
              )}

              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {courses.map((c) => {
                  const idx = selectedCourseIds.indexOf(c.id)
                  const selected = idx >= 0
                  const full = selectedCourseIds.length >= needCoursesMax && !selected
                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={full || c.status === 'finished'}
                      onClick={() => toggleCourse(c.id)}
                      className={`w-full text-right text-sm px-3 py-2 rounded-lg flex items-center gap-2 transition ${
                        selected
                          ? 'bg-primary/25 border border-primary/50'
                          : c.status === 'finished'
                            ? 'opacity-40 border border-transparent'
                            : 'border border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                          selected ? 'bg-primary text-white' : 'bg-gray-800 text-gray-500'
                        }`}
                      >
                        {selected ? idx + 1 : '+'}
                      </span>
                      <span className="flex-1 min-w-0 truncate">{c.name}</span>
                      <StatusBadge status={c.status} />
                    </button>
                  )
                })}
              </div>
            </div>

            {roundMode === 'three_in_four' && selectedCourseIds.length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-3">
                <div>
                  <p className="text-sm font-medium text-amber-200">اسب‌های فعال (سه در چهار)</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    از هر کورس دقیقاً ۴ اسب انتخاب کن (جمع ۱۶). کاربر در هر کورس ترتیب اول تا چهارم همان ۴ اسب
                    را مشخص می‌کند.
                  </p>
                </div>
                {selectedCoursesOrdered.map((c, i) => {
                  const locked = lockedByCourse[c.id] || []
                  return (
                    <div key={c.id}>
                      <p className="text-xs text-gray-300 mb-1.5">
                        {i + 1}. {c.name}
                        <span
                          className={`mr-2 ${locked.length === 4 ? 'text-emerald-400' : 'text-amber-400'}`}
                        >
                          {' '}
                          {locked.length}/۴
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {(c.horses || []).map((h) => (
                          <button
                            key={h.id}
                            type="button"
                            onClick={() => toggleLockedHorse(c.id, h.id)}
                            className={`text-xs px-2.5 py-1.5 rounded-lg border ${
                              locked.includes(h.id)
                                ? 'border-primary bg-primary/30'
                                : 'border-gray-600'
                            }`}
                          >
                            {h.photo_url && (
                              <img src={h.photo_url} alt="" className="w-5 h-5 rounded object-cover inline-block align-middle ml-1" />
                            )}
                            #{h.number} {h.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="text-xs text-gray-500 bg-gray-900/50 rounded-lg p-2">
              حالت انتخابی: <strong className="text-gray-300">{modeMeta.label}</strong> —{' '}
              {modeMeta.hint}
            </div>

            <Button
              disabled={
                busy ||
                selectedCourseIds.length < needCoursesMin ||
                selectedCourseIds.length > needCoursesMax
              }
              onClick={createRound}
            >
              {busy
                ? 'در حال ساخت…'
                : selectedCourseIds.length < needCoursesMin
                  ? `حداقل ${needCoursesMin} کورس لازم است (${selectedCourseIds.length}/${needCoursesMax})`
                  : 'باز کردن نظرسنجی برای کاربران'}
            </Button>
          </Card>
        </div>
      )}

      {/* RESULTS */}
      {tab === 'results' && (
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="font-bold">ثبت مقام اسب‌ها</h3>
            <p className="text-xs text-gray-400">
              کورس را انتخاب کن و برای هر اسب مقام بگذار. مقام‌ها خودکار جابه‌جا می‌شوند تا تکراری
              نباشند.
            </p>

            <div>
              <FieldLabel>کورس</FieldLabel>
              <select
                className={inputClass}
                value={resultCourseId}
                onChange={(e) => openResultEditor(Number(e.target.value))}
              >
                <option value={0}>— انتخاب کن —</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({STATUS_LABEL[c.status]?.text || c.status}) · {c.horses?.length || 0}{' '}
                    اسب
                  </option>
                ))}
              </select>
            </div>

            {resultCourse?.horses && resultCourse.horses.length > 0 && (
              <>
                <div className="space-y-2">
                  {sortedHorsesForResult(resultCourse.horses).map((h) => (
                    <div
                      key={h.id}
                      className="flex items-center gap-2 border border-gray-700 rounded-xl p-2.5"
                    >
                      <select
                        className="bg-gray-900 border border-gray-600 rounded-lg px-2 py-1.5 text-sm font-bold w-20"
                        value={placeByHorse[h.id] || 1}
                        onChange={(e) => setPlace(h.id, Number(e.target.value))}
                        disabled={resultCourse.status === 'finished'}
                      >
                        {resultCourse.horses!.map((_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </select>
                      {h.photo_url && (
                        <img src={h.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          #{h.number} {h.name}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          مقام {placeByHorse[h.id] || '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {resultCourse.status === 'finished' ? (
                  <p className="text-sm text-emerald-300 text-center py-2">
                    نتیجه این کورس قبلاً ثبت شده است
                  </p>
                ) : (
                  <Button disabled={busy} onClick={submitResult}>
                    {busy ? 'در حال ثبت…' : 'ثبت نتیجه نهایی'}
                  </Button>
                )}
              </>
            )}

            {!resultCourseId && upcomingCourses.length > 0 && (
              <div className="space-y-1.5 pt-1">
                <p className="text-xs text-gray-500">یا سریع از لیست:</p>
                {upcomingCourses.slice(0, 8).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => openResultEditor(c.id)}
                    className="w-full text-right text-sm border border-gray-700 rounded-lg px-3 py-2 hover:border-gray-500"
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ROUNDS / SETTLE */}
      {tab === 'rounds' && (
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="font-bold">نظرسنجی‌های فعال</h3>
            <p className="text-xs text-gray-400">
              بعد از ثبت نتیجهٔ همه کورس‌های یک دور، دکمه تسویه را بزن تا امتیاز و جایزه داده شود.
            </p>
            {openRounds.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">نظرسنجی بازی نیست</p>
            )}
            {openRounds.map((r) => (
              <RoundCard
                key={r.id}
                round={r}
                busy={busy}
                onCloseVoting={() => closeVoting(r.id)}
                onSettle={() => settle(r.id)}
              />
            ))}
          </Card>

          {settledRounds.length > 0 && (
            <Card className="p-4 space-y-2">
              <h3 className="font-bold text-sm text-gray-400">تسویه‌شده‌ها</h3>
              {settledRounds.slice(0, 10).map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between text-sm border border-gray-800 rounded-lg px-3 py-2 opacity-70"
                >
                  <span>
                    #{r.id} {MODE_LABEL[r.mode] || r.title}
                  </span>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </Card>
          )}

          <Card className="p-4 space-y-2">
            <h3 className="font-bold text-sm">جایزه ماهانه هورس لیگ</h3>
            <p className="text-xs text-gray-400">
              فقط خودکار توسط سرور انجام می‌شود: اول هر ماه (ساعت پنجره بازی) به ۶ نفر برتر ماه قبل،
              هر کدام ۱ سکه برنز. ادمین دستی این کار را انجام نمی‌دهد.
            </p>
          </Card>
        </div>
      )}
    </div>
  )
}

function RoundCard({
  round,
  busy,
  onCloseVoting,
  onSettle,
}: {
  round: HorsePollRound
  busy: boolean
  onCloseVoting: () => void
  onSettle: () => void
}) {
  const courses = round.courses || []
  const readyCount = courses.filter((rc) => rc.course?.status === 'finished').length
  const allReady = courses.length > 0 && readyCount === courses.length
  const votingOpen = Boolean(round.voting_open)

  return (
    <div className="border border-gray-700 rounded-xl p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium text-sm">
            #{round.id} {MODE_LABEL[round.mode] || round.title}
          </p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {round.entry_fee_bronze > 0
              ? `ورودی ${round.entry_fee_bronze} برنز یا ${round.entry_ads_required || (round.mode === 'more_chance' ? 3 : 5)} ویدیو`
              : 'رایگان'}{' '}
            · {courses.length} کورس · {round.entry_count ?? 0} شرکت‌کننده
            {votingOpen ? ' · ثبت نظر باز' : ' · ثبت نظر بسته'}
          </p>
        </div>
        <StatusBadge status={round.status} />
      </div>

      <div className="space-y-1">
        {courses.map((rc, i) => (
          <div
            key={rc.id}
            className="flex items-center justify-between text-[11px] text-gray-400 px-1"
          >
            <span>
              {i + 1}. {rc.course?.name || `کورس ${rc.course_id}`}
            </span>
            <StatusBadge status={rc.course?.status || 'upcoming'} />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
        <span
          className={`text-[11px] me-auto ${allReady ? 'text-emerald-400' : 'text-amber-300'}`}
        >
          نتایج: {readyCount}/{courses.length}
          {!allReady && ' — هنوز کامل نیست'}
        </span>
        {round.status === 'open' && (
          <button
            type="button"
            disabled={busy}
            title="بستن ثبت نظر برای کاربران"
            onClick={onCloseVoting}
            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-amber-500/40 text-amber-200 disabled:opacity-40"
          >
            بستن ثبت نظر
          </button>
        )}
        <button
          type="button"
          disabled={busy || !allReady}
          onClick={onSettle}
          className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
            allReady
              ? 'bg-primary text-white'
              : 'bg-gray-800 text-gray-500 cursor-not-allowed'
          }`}
        >
          تسویه و جایزه
        </button>
      </div>
    </div>
  )
}
